import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { Match } from '../types';

export default function LobbyPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMatch, setNewMatch] = useState({
    name: '',
    description: '',
    maxPlayers: 4,
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: matches, isLoading } = useQuery({
    queryKey: ['matches'],
    queryFn: () => apiClient.getMatches(),
  });

  const createMatchMutation = useMutation({
    mutationFn: (match: Partial<Match>) => apiClient.createMatch(match),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      setShowCreateForm(false);
      setNewMatch({ name: '', description: '', maxPlayers: 4 });
      navigate(`/game/${data.id}`);
    },
  });

  const joinMatchMutation = useMutation({
    mutationFn: (id: number) => apiClient.joinMatch(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      navigate(`/game/${data.id}`);
    },
  });

  const handleCreateMatch = (e: React.FormEvent) => {
    e.preventDefault();
    createMatchMutation.mutate(newMatch);
  };

  const handleJoinMatch = (matchId: number) => {
    joinMatchMutation.mutate(matchId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading matches...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-responsive-xl font-bold text-white">Game Lobby</h1>
          <p className="mt-1 text-sm text-zinc-400">
            Join existing matches or create your own
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn-primary w-full sm:w-auto"
        >
          Create New Match
        </button>
      </div>

      {/* Create Match Form */}
      {showCreateForm && (
        <div className="mobile-card">
          <h3 className="text-responsive-lg font-medium text-white mb-4">Create New Match</h3>
          <form onSubmit={handleCreateMatch} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-zinc-300 mb-2">
                Match Name
              </label>
              <input
                type="text"
                id="name"
                value={newMatch.name}
                onChange={(e) => setNewMatch({ ...newMatch, name: e.target.value })}
                className="input-field"
                placeholder="Enter match name"
                required
              />
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-zinc-300 mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={newMatch.description}
                onChange={(e) => setNewMatch({ ...newMatch, description: e.target.value })}
                className="input-field min-h-[100px] resize-none"
                rows={3}
                placeholder="Enter match description"
              />
            </div>
            <div>
              <label htmlFor="maxPlayers" className="block text-sm font-medium text-zinc-300 mb-2">
                Max Players
              </label>
              <select
                id="maxPlayers"
                value={newMatch.maxPlayers}
                onChange={(e) => setNewMatch({ ...newMatch, maxPlayers: parseInt(e.target.value) })}
                className="input-field"
              >
                <option value={2}>2 Players</option>
                <option value={3}>3 Players</option>
                <option value={4}>4 Players</option>
                <option value={5}>5 Players</option>
                <option value={6}>6 Players</option>
              </select>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button type="submit" className="btn-primary flex-1" disabled={createMatchMutation.isPending}>
                {createMatchMutation.isPending ? 'Creating...' : 'Create Match'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary flex-1 sm:flex-none"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Matches List */}
      <div className="space-y-4">
        {matches?.map((match) => (
          <div key={match.id} className="mobile-card">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-responsive-lg font-medium text-white truncate">{match.name}</h3>
                <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{match.description}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    match.status === 'WAITING' 
                      ? 'bg-yellow-400/20 text-yellow-400 border border-yellow-400/30'
                      : match.status === 'IN_PROGRESS'
                      ? 'bg-green-400/20 text-green-400 border border-green-400/30'
                      : 'bg-zinc-400/20 text-zinc-400 border border-zinc-400/30'
                  }`}>
                    {match.status.replace('_', ' ')}
                  </span>
                  <span className="text-sm text-zinc-400">
                    {match.currentPlayers}/{match.maxPlayers} players
                  </span>
                  <span className="text-sm text-zinc-400">
                    {new Date(match.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {match.players && match.players.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-zinc-400">Players:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {match.players.map((player) => (
                        <span
                          key={player.id}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-white/10 text-zinc-300 border border-white/20"
                        >
                          {player.username}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
                {match.status === 'WAITING' && match.currentPlayers < match.maxPlayers && (
                  <button
                    onClick={() => handleJoinMatch(match.id)}
                    className="btn-primary flex-1 sm:flex-none"
                    disabled={joinMatchMutation.isPending}
                  >
                    {joinMatchMutation.isPending ? 'Joining...' : 'Join'}
                  </button>
                )}
                <button
                  onClick={() => navigate(`/game/${match.id}`)}
                  className="btn-secondary flex-1 sm:flex-none"
                >
                  View
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {matches?.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-white">No matches</h3>
          <p className="mt-1 text-sm text-zinc-400">Get started by creating a new match.</p>
        </div>
      )}
    </div>
  );
}
